import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from './ui/button'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { Textarea } from './ui/textarea'
import { useAsset, useAssetProperties, isPendingApprovalValue, useSetAssetPropertyValue, useUpdateAssetPropertyValue, useDeleteAssetPropertyValue, useConfirmAssetPropertyValue, usePropertyDefinitions, usePropertyDefinitionsByUids, usePropertyDefinition, queryKeys, type PropertyDefinition } from '../hooks/useQueries'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { AppIcon } from './ui/icon'
import { mdiAlertOutline, mdiCheck, mdiChevronDown, mdiClose, mdiDelete, mdiEye, mdiEyeOff, mdiHistory, mdiLock, mdiOpenInNew, mdiPlus } from '@mdi/js'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useApi } from '../api/hooks'
import { apiClient } from '../api/client'
import { $api } from '../api/queryClient'
import { getBooleanOptions, getPropertyConstraints, EnumPropertyDisplay, EnumPropertyInput, useEnumValueText } from './properties/PropertyInputUtils'
import { PropertyValueDisplay, PropertyValueEditInput, formatPropertyValue, usesDialogEditor, type PropertySibling } from './properties/PropertyValueCell'
import { useCan } from '../contexts/PermissionsContext'
import { PropertyHistoryDrawer } from './properties/PropertyHistoryDrawer'
import { PropertySourceBadge } from './PropertySourceBadge'
import { useFocusAndPulseRow } from '../hooks/useFocusAndPulseRow'
import { useAssetDescendantUids } from '../hooks/useAssetDescendantUids'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { AssetTypePicker } from './asset-type-picker/AssetTypePicker'
import { EmptyState } from './ui/empty-state'

/**
 * A synthetic "core" row for the Data tab's grouped mode. These are asset
 * columns (external code, lifecycle dates) — NOT property definitions — surfaced
 * as editable rows at the top of their section and saved by PATCHing the asset
 * via `onSaveCoreRow`, not the properties API.
 */

/** The meter index is entered on MeterReadingsCard as a dated series (spec 2026-10-27-18). */
const SERIES_ONLY_PROPERTY = 'meter_reading'

export interface CoreRow {
    code: string
    category: string
    label: string
    /**
     * 'asset_type' renders the AssetTypePicker and 'asset' an asset search —
     * both carry a uid in `value`, not a label. 'number' is a plain numeric
     * input (quantity).
     */
    dataType: 'date' | 'text' | 'number' | 'asset_type' | 'asset'
    value: string | null
    /** Human label for a uid-valued row ('asset'), so the cell can show a name
     * without resolving the uid itself. */
    valueLabel?: string | null
    /** The row is shown but cannot be edited here (e.g. a quantity on a type
     * tracked one asset per unit). `hint` says why, under the value. */
    readOnly?: boolean
    hint?: string | null
}

interface AssetPropertiesProps {
    org: string
    assetUid: string
    onPropertyUpdated?: () => void
    /**
     * Restrict the rendered list and the "Add property" dropdown to
     * property definitions whose `property_category_code` is in this set.
     * When omitted, behaves as today (shows everything).
     *
     * Used by spec 2026-05-06-02 to route property categories to context
     * tabs (Compliance, Equipment, Plan) and keep the catch-all in the
     * Properties tab.
     */
    categoryCodes?: string[]
    /**
     * Rendering style.
     *   - 'table' (default): the classic dense properties table.
     *   - 'reliability': the Fiabilisation module's "Validé automatiquement"
     *     collapsible — one editable row per property (name + value + source),
     *     folded away by default. Reuses every edit handler; only the chrome
     *     and row layout differ.
     */
    variant?: 'table' | 'reliability'
    /** Open the collapsible by default (reliability variant only). */
    defaultOpen?: boolean
    /**
     * Data-tab grouped mode. When true (table variant only), rows are grouped
     * under localized category section headings instead of a single flat table.
     */
    groupByCategory?: boolean
    /**
     * Ordered category codes controlling section order in grouped mode. Any
     * category with values not listed here is appended after, in first-seen
     * order.
     */
    categoryOrder?: string[]
    /**
     * Base property-definition codes for this asset type. In grouped mode, base
     * codes with no value yet are rendered as empty "add value" ghost rows so
     * the full expected set stays visible and fillable. Rows are ordered by
     * this list within each category.
     */
    baseCodes?: string[]
    /**
     * Optional per-code metadata for base properties: the resolved requirement
     * level and expected_source hint (assigned | diagnosed | measured |
     * declared). In grouped mode, mandatory base rows are flagged and the
     * expected source is shown as a small badge next to the property name.
     */
    baseMeta?: Record<string, { requirement?: string; expectedSource?: string; section?: string }>
    /**
     * Data-tab grouped mode: when true, show ONLY the base-code properties
     * (as value or ghost rows). Any other stored value on the asset — e.g. a
     * derived/computed property — is hidden, so the tab reflects exactly the
     * resolved socle.
     */
    restrictToBaseCodes?: boolean
    /**
     * Data-tab opt-in: when true (grouped mode only), the empty "add value"
     * ghost rows are hidden by default so sparse assets show only filled
     * properties. A toggle ("Show all properties") reveals them, and the state
     * is mirrored in the `?show=all` URL search param so it deep-links.
     *
     * Off by default so every other caller (onboarding-style questionnaires,
     * Compliance/Plan/Properties tabs, reliability variant) keeps ghost rows
     * visible where adding values is the point. See spec
     * 2026-07-09-data-tab-hide-empty-rows.
     */
    hideEmptyByDefault?: boolean
    /**
     * Grouped mode only: synthetic asset-column rows (external code, lifecycle
     * dates) injected at the top of their section. Edited via `onSaveCoreRow`
     * (PATCH on the asset), not the properties API.
     */
    coreRows?: CoreRow[]
    /** Persist a core-row edit. Resolves once the asset PATCH completes. */
    onSaveCoreRow?: (code: string, value: string | null) => Promise<void>
}

// Legal axis properties that gate which `cb_*` / `mfr_*` / `sfh_*`
// properties are valid on the asset. They must appear first in the
// Properties tab so users see them before any property whose validity
// depends on them.
const LEGAL_GATING_CODES = ['legal_property_type', 'legal_ownership_status']

export function AssetProperties({ org, assetUid, onPropertyUpdated, categoryCodes, variant = 'table', defaultOpen = false, groupByCategory = false, categoryOrder, baseCodes, baseMeta, restrictToBaseCodes = false, hideEmptyByDefault = false, coreRows, onSaveCoreRow }: AssetPropertiesProps) {
    const queryClient = useQueryClient()
    const { t, i18n } = useTranslation('assets')
    const { t: tReliability } = useTranslation('data-reliability')
    const { t: tData } = useTranslation('data')
    const { t: tCommon } = useTranslation('common')
    const { request } = useApi()
    // Self + subtree: what a parent picker must never offer.
    const excludedParentUids = useAssetDescendantUids(org, assetUid)
    // Editing an asset's properties is `assets.write`. When it is missing the
    // rows stay on screen and stay readable — only the edit affordances go
    // (spec 2026-09-04-02: "inline editors render read-only").
    const canWriteAssets = useCan('assets.write')

    // Reliability variant: whether the "Validé automatiquement" fold is open.
    const [validatedOpen, setValidatedOpen] = useState(defaultOpen)

    // Properties state
    const [showAddPropertyForm, setShowAddPropertyForm] = useState(false)
    const [newPropertyDefinitionUid, setNewPropertyDefinitionUid] = useState('')
    const [newPropertyValue, setNewPropertyValue] = useState('')
    const [addPropertyError, setAddPropertyError] = useState<string | null>(null)
    const [editingPropertyUid, setEditingPropertyUid] = useState<string | null>(null)
    const [editingPropertyValue, setEditingPropertyValue] = useState('')
    const [propertyEditErrors, setPropertyEditErrors] = useState<Record<string, string>>({})
    // Core-row (asset column) inline editing — external code, lifecycle dates.
    const [editingCoreCode, setEditingCoreCode] = useState<string | null>(null)
    const [editingCoreValue, setEditingCoreValue] = useState('')
    const [coreSaving, setCoreSaving] = useState(false)
    const [coreError, setCoreError] = useState<string | null>(null)
    const [propertyToDelete, setPropertyToDelete] = useState<string | null>(null)

    // Load asset properties (includes pending-approval values via include_pending=true)
    const { data: allProperties = [], isLoading: propertiesLoading } = useAssetProperties(org, assetUid)
    const properties = useMemo(() => allProperties.filter(p => !isPendingApprovalValue(p)), [allProperties])
    const pendingValues = useMemo(() => allProperties.filter(p => isPendingApprovalValue(p)), [allProperties])
    // Pending values count too: a property whose only value awaits approval
    // still needs its definition, or its conflict badge prints a raw code.
    const propertyDefinitionUids = useMemo(() =>
        [...new Set(allProperties.map(p => p.property_uid).filter((uid): uid is string => Boolean(uid)))],
        [allProperties]
    )
    const { data: propertyDefinitions = [] } = usePropertyDefinitionsByUids(org, propertyDefinitionUids)

    // Open declared-vs-observed reconciliation issues targeting this asset
    // (spec 2026-07-24-04-declared-vs-observed-property-reconciliation.md).
    // `assignee_uid=all` bypasses the "current user" default — these issues
    // are auto-created and typically unassigned.
    const { data: reconciliationIssuesData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        {
            params: {
                path: { orgCode: org },
                query: {
                    target_entity_type: 'asset',
                    target_entity_uid: assetUid,
                    status: 'open',
                    type: 'data_consistency',
                    assignee_uid: 'all',
                    limit: 100,
                },
            },
        },
        { enabled: Boolean(assetUid) },
    )

    // Keyed by the declared property's code — the badge is rendered next to
    // that property's row, showing the observed value and linking to the
    // issue for the "Adopt observed" / "Complete the inventory" actions.
    const reconciliationByCode = useMemo(() => {
        const map = new Map<string, { issueUid: string; observedValue: number }>()
        for (const issue of reconciliationIssuesData?.data ?? []) {
            const props = issue.properties as
                | { check?: string; declared_property_code?: string; observed_value?: number }
                | undefined
            if (props?.check !== 'property_reconciliation' || !props.declared_property_code) continue
            if (!issue.uid) continue
            map.set(props.declared_property_code, {
                issueUid: issue.uid,
                observedValue: props.observed_value ?? 0,
            })
        }
        return map
    }, [reconciliationIssuesData])

    // Pending `to_approve` candidates keyed by property definition uid — used by
    // the Data tab to flag value conflicts and drive the conflict drawer.
    const pendingByDef = useMemo(() => {
        const map = new Map<string, typeof pendingValues>()
        for (const p of pendingValues) {
            const key = p.property_uid
            if (!key) continue
            if (!map.has(key)) map.set(key, [])
            map.get(key)!.push(p)
        }
        return map
    }, [pendingValues])

    // Property history drawer: driven by the `propertyHistory` URL search param
    // on the asset detail route. Opening pushes the param, closing clears it,
    // and reloading the page re-opens the drawer at the same state.
    const navigate = useNavigate({ from: '/$org/assets/$assetUid' })
    const search = useSearch({ strict: false }) as { propertyHistory?: string; show?: string; focusProperty?: string }
    const historyPropertyUid = search.propertyHistory

    // "Back to its property" side of a document highlight click (user,
    // 2026-09-16): `?focusProperty=<uid>` scrolls that row into view and
    // pulses it. See useFocusAndPulseRow and the `asset-property-row-*`
    // testid on the "value" row below.
    useFocusAndPulseRow(search.focusProperty ? `asset-property-row-${search.focusProperty}` : undefined)

    // Data-tab hide-empty toggle. Mirrored in the `?show=all` URL search param so
    // it deep-links (loading with `?show=all` starts expanded). When the caller
    // did not opt into hiding, empties are always shown (current behavior).
    const showAll = hideEmptyByDefault ? search.show === 'all' : true
    const setShowAll = useCallback((all: boolean) => {
        navigate({
            search: (prev: Record<string, unknown>) => {
                if (all) return { ...prev, show: 'all' }
                const { show: _unused, ...rest } = prev
                return rest
            },
            replace: true,
        })
    }, [navigate])
    const openHistoryDrawer = useCallback((propertyUid: string) => {
        navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, propertyHistory: propertyUid }), replace: false })
    }, [navigate])
    const closeHistoryDrawer = useCallback(() => {
        navigate({
            search: (prev: Record<string, unknown>) => {
                const { propertyHistory: _unused, ...rest } = prev
                return rest
            },
            replace: false,
        })
    }, [navigate])

    // Property mutations
    const setPropertyValueMutation = useSetAssetPropertyValue(org, assetUid)
    const updatePropertyValueMutation = useUpdateAssetPropertyValue(org, assetUid)
    const deletePropertyValueMutation = useDeleteAssetPropertyValue(org, assetUid)
    const confirmPropertyValueMutation = useConfirmAssetPropertyValue(org, assetUid)

    // The edited asset's own type and current values feed the value suggestions
    // of text properties declaring `properties.suggestions` (spec 2026-09-02-03).
    const { data: assetForSuggestions } = useAsset(org, assetUid)
    const assetTypeUid = (assetForSuggestions as { asset_type_uid?: string } | undefined)?.asset_type_uid

    // Helper functions
    const getPropertyDefinition = useCallback((propertyDefinitionUid: string) => {
        return propertyDefinitions.find(pd => pd.uid === propertyDefinitionUid)
    }, [propertyDefinitions])

    const getPropertyDefinitionName = useCallback((propertyDefinitionUid: string) => {
        const propDef = getPropertyDefinition(propertyDefinitionUid)
        if (!propDef) return propertyDefinitionUid

        const locale = i18n.language
        if (typeof propDef.name === 'object' && propDef.name !== null) {
            const names = propDef.name as Record<string, string>
            return names[locale] || names.en || names.fr || Object.values(names)[0] || propDef.code || propertyDefinitionUid
        }

        return propDef.name || propDef.code || propertyDefinitionUid
    }, [getPropertyDefinition, i18n.language])

    // Visible properties — the rows that actually make it into the table
    // after applying the always-hidden filter, the system "hidden" flag, and
    // the optional category restriction. Memoised so we can use its length
    // both to decide whether to render the table at all (avoiding a
    // header-only empty table) and to drive the row map without recomputing.
    const visibleProperties = useMemo(() => {
        const filtered = properties.filter((property) => {
            const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
            if (propDef?.flags?.includes('hidden')) return false
            if (['geolocation', 'address_iso20022', 'address_raw', 'google_place_id'].includes(propDef?.code || '')) return false
            if (categoryCodes && categoryCodes.length > 0) {
                const cat = (propDef as { property_category_code?: string } | undefined)?.property_category_code
                if (!cat || !categoryCodes.includes(cat)) return false
            }
            return true
        })
        filtered.sort((a, b) => {
            const aDef = propertyDefinitions.find(pd => pd.uid === a.property_uid)
            const bDef = propertyDefinitions.find(pd => pd.uid === b.property_uid)
            const aIdx = aDef ? LEGAL_GATING_CODES.indexOf(aDef.code || '') : -1
            const bIdx = bDef ? LEGAL_GATING_CODES.indexOf(bDef.code || '') : -1
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
            if (aIdx !== -1) return -1
            if (bIdx !== -1) return 1
            return 0
        })
        return filtered
    }, [properties, propertyDefinitions, categoryCodes])

    // Property definition search for add form
    const { data: propertyDefinitionsAll = [] } = usePropertyDefinitions(org)
    const propertyDefinitionInitialOptions: ApiSearchableSelectOption[] = useMemo(() => {
        const locale = i18n.language
        return propertyDefinitionsAll
            .filter(pd => !pd.flags?.includes('system_managed'))
            .filter(pd => {
                // Match the rendered-list category filter so the add dropdown
                // only offers properties that would land in this tab. When
                // `categoryCodes` is unset, all definitions are offered.
                if (!categoryCodes || categoryCodes.length === 0) return true
                const cat = (pd as { property_category_code?: string }).property_category_code
                return cat ? categoryCodes.includes(cat) : false
            })
            .map(pd => {
            const names = typeof pd.name === 'object' && pd.name !== null ? pd.name as Record<string, string> : null
            const name = names
                ? names[locale] || names.en || names.fr || Object.values(names)[0] || pd.code || pd.uid
                : pd.name || pd.code || pd.uid
            // Build label with flags
            const flags = []
            if (pd.data_type === 'enum' && pd.properties?.multiple === true) {
                flags.push(t('detail.flag_multiple'))
            }
            if ((pd.data_type === 'boolean' || pd.data_type === 'enum') && pd.flags?.includes('attribute')) {
                flags.push(t('detail.flag_attribute'))
            }
            const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''
            return {
                value: pd.uid || '',
                label: `${name} (${pd.data_type})${flagsStr}`
            }
        })
    }, [propertyDefinitionsAll, i18n.language, t, categoryCodes])

    // Keep track of additional options that have been selected from API search
    const [additionalOptions, setAdditionalOptions] = useState<ApiSearchableSelectOption[]>([])

    // Localized name for any definition (works for ghost/base rows whose defs
    // aren't in the by-uid list because they have no value yet).
    const localizeDefName = useCallback((def?: PropertyDefinition) => {
        if (!def) return ''
        const locale = i18n.language
        if (typeof def.name === 'object' && def.name !== null) {
            const names = def.name as Record<string, string>
            return names[locale] || names.en || names.fr || Object.values(names)[0] || def.code || ''
        }
        return def.name || def.code || ''
    }, [i18n.language])

    // Data-tab grouped mode: build ordered category sections. Each section lists
    // its base codes first (in order) — as a value row when filled, or a ghost
    // "add value" row when empty — then any remaining filled properties of that
    // category. Categories follow `categoryOrder`, with any extras appended.
    const groupedSections = useMemo(() => {
        if (!groupByCategory) return [] as { category: string; rows: Array<{ kind: 'core'; core: CoreRow } | { kind: 'value'; property: any } | { kind: 'ghost'; def: PropertyDefinition }> }[]
        const defByCode = new Map<string, PropertyDefinition>()
        for (const d of propertyDefinitionsAll) if (d.code) defByCode.set(d.code, d)
        const valueByDefUid = new Map<string, any>()
        for (const p of visibleProperties) if (p.property_uid) valueByDefUid.set(p.property_uid, p)
        const catOf = (p: any) => {
            const def = propertyDefinitions.find(pd => pd.uid === p.property_uid)
            return (def as { property_category_code?: string } | undefined)?.property_category_code || 'other'
        }

        // Section of a base code: the explicit section from baseMeta (identity /
        // compliance) takes precedence over the property's fine category, so the
        // Data tab groups into the two socle sections.
        const sectionOfCode = (code: string) => {
            const s = baseMeta?.[code]?.section
            if (s) return s
            const def = defByCode.get(code)
            return (def as { property_category_code?: string } | undefined)?.property_category_code || 'other'
        }

        const baseByCat = new Map<string, string[]>()
        for (const code of baseCodes || []) {
            const cat = sectionOfCode(code)
            if (!baseByCat.has(cat)) baseByCat.set(cat, [])
            baseByCat.get(cat)!.push(code)
        }

        const order = categoryOrder && categoryOrder.length ? [...categoryOrder] : []
        const seen = new Set(order)
        // When restricted to the socle, only base codes drive the sections, plus
        // the values a derivation computed: they have no other place on the
        // page (a building's cb_heating_type read from its equipment, spec
        // 2026-10-06-18). Other stored values are not shown here.
        const shownOutsideSocle = (p: any) => !restrictToBaseCodes || p.source_code === 'derivation'
        for (const p of visibleProperties) {
            if (!shownOutsideSocle(p)) continue
            const c = catOf(p)
            if (!seen.has(c)) { seen.add(c); order.push(c) }
        }
        for (const c of baseByCat.keys()) if (!seen.has(c)) { seen.add(c); order.push(c) }
        for (const cr of coreRows || []) if (!seen.has(cr.category)) { seen.add(cr.category); order.push(cr.category) }

        const sections: { category: string; rows: Array<{ kind: 'core'; core: CoreRow } | { kind: 'value'; property: any } | { kind: 'ghost'; def: PropertyDefinition }> }[] = []
        const usedDefUids = new Set<string>()
        for (const cat of order) {
            const rows: Array<{ kind: 'core'; core: CoreRow } | { kind: 'value'; property: any } | { kind: 'ghost'; def: PropertyDefinition }> = []
            // Core asset-column rows lead their section (e.g. external code +
            // lifecycle dates at the top of Identité).
            for (const cr of coreRows || []) if (cr.category === cat) rows.push({ kind: 'core', core: cr })
            for (const code of baseByCat.get(cat) || []) {
                const def = defByCode.get(code)
                if (!def) continue
                const existing = def.uid ? valueByDefUid.get(def.uid) : undefined
                if (existing) { rows.push({ kind: 'value', property: existing }); if (def.uid) usedDefUids.add(def.uid) }
                else rows.push({ kind: 'ghost', def })
            }
            for (const p of visibleProperties) {
                if (!shownOutsideSocle(p) || catOf(p) !== cat) continue
                if (p.property_uid && usedDefUids.has(p.property_uid)) continue
                rows.push({ kind: 'value', property: p })
                if (p.property_uid) usedDefUids.add(p.property_uid)
            }
            if (rows.length) sections.push({ category: cat, rows })
        }
        return sections
    }, [groupByCategory, propertyDefinitionsAll, visibleProperties, propertyDefinitions, baseCodes, categoryOrder, baseMeta, restrictToBaseCodes, coreRows])

    // A ghost row is "empty" (hideable) only when it carries no pending conflict.
    // Ghost rows with a proposed value stay visible even when collapsed — they
    // are actionable arbitrations, not empty add-value rows.
    const isEmptyGhost = useCallback((row: { kind: 'core'; core: CoreRow } | { kind: 'value'; property: any } | { kind: 'ghost'; def: PropertyDefinition }) => {
        return row.kind === 'ghost' && !(row.def.uid && (pendingByDef.get(row.def.uid)?.length))
    }, [pendingByDef])

    const hiddenEmptyCount = useMemo(
        () => groupedSections.reduce((n, s) => n + s.rows.filter(isEmptyGhost).length, 0),
        [groupedSections, isEmptyGhost]
    )

    // When collapsed (show=filled), drop empty ghost rows and any section left
    // with no rows. When expanded (show=all) or when the caller did not opt into
    // hiding, render the full set unchanged.
    const displaySections = useMemo(() => {
        if (showAll) return groupedSections
        return groupedSections
            .map(s => ({ category: s.category, rows: s.rows.filter(r => !isEmptyGhost(r)) }))
            .filter(s => s.rows.length > 0)
    }, [groupedSections, showAll, isEmptyGhost])

    const searchPropertyDefinitions = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            // Use API search with query parameter
            const response = await request('/api/v1/organizations/{orgCode}/properties', 'GET', {
                params: {
                    path: {
                        orgCode: org
                    },
                    query: {
                        q: query,
                        asset_uid: assetUid,
                        limit: 50
                    }
                }
            })

            // The search results are directly in the data property of the response
            const searchResults = ((response as { data?: PropertyDefinition[] })?.data || [])
            const locale = i18n.language
            return searchResults.map((propDef: PropertyDefinition) => {
                // Extract name directly from the search result instead of using getPropertyDefinitionName
                const names = typeof propDef.name === 'object' && propDef.name !== null ? propDef.name as Record<string, string> : null
                const name = names
                    ? names[locale] || names.en || names.fr || Object.values(names)[0] || propDef.code || propDef.uid
                    : propDef.name || propDef.code || propDef.uid

                // Build label with flags
                const flags = []
                if (propDef.data_type === 'enum' && propDef.properties?.multiple === true) {
                    flags.push(t('detail.flag_multiple'))
                }
                if ((propDef.data_type === 'boolean' || propDef.data_type === 'enum') && propDef.flags?.includes('attribute')) {
                    flags.push(t('detail.flag_attribute'))
                }
                const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''

                return {
                    value: propDef.uid || '',
                    label: `${name} (${propDef.data_type})${flagsStr}`
                }
            })
        } catch (error) {
            console.error('Error searching property definitions:', error)
            // Fallback to local filtering if API fails
            const filtered = propertyDefinitionInitialOptions.filter(option =>
                option.label.toLowerCase().includes(query.toLowerCase())
            )
            return filtered.slice(0, 50)
        }
    }, [propertyDefinitionInitialOptions, org, assetUid, request, i18n.language, t])

    // Property value display and editing
    const { data: selectedPropertyDef } = usePropertyDefinition(org, newPropertyDefinitionUid)

    // Property display with proper type handling
    const getPropertyDisplayValue = useCallback((value: unknown, propertyUid?: string) => {
        const propDef = propertyUid ? propertyDefinitions.find(pd => pd.uid === propertyUid) : undefined
        return formatPropertyValue(value, propDef, tCommon)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyDefinitions])


    // Property editing handlers
    const startEditingProperty = useCallback((property: any) => {
        // Format initial value based on property type
        const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
        // A meter's index is a dated series: it is entered on the meter
        // readings card, never overwritten in place (spec 2026-10-27-18).
        if (propDef?.code === SERIES_ONLY_PROPERTY) return
        setEditingPropertyUid(property.property_uid)
        let initialValue = ''

        // Handle enum arrays - convert to comma-separated string for editing
        if (propDef?.data_type === 'enum' && propDef.properties?.multiple === true && Array.isArray(property.value)) {
            initialValue = property.value.join(', ')
        } else if (property.value !== null && property.value !== undefined) {
            initialValue = String(property.value)
        }

  
      // Format date values for input fields
        if (propDef?.data_type === 'date' && initialValue) {
            try {
                const date = new Date(initialValue)
                if (!isNaN(date.getTime())) {
                    // If with_time is enabled, format as datetime-local, otherwise as date
                    if (propDef.properties?.with_time === true) {
                        // Format for datetime-local input in local timezone (YYYY-MM-DDTHH:mm)
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        const hours = String(date.getHours()).padStart(2, '0')
                        const minutes = String(date.getMinutes()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}T${hours}:${minutes}`
                    } else {
                        // Format for date input (YYYY-MM-DD) in local timezone
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}`
                    }
                }
            } catch {
                // Keep original value if date parsing fails
            }
        }

        setEditingPropertyValue(initialValue)
        setPropertyEditErrors({})
    }, [propertyDefinitions])

    const handleCancelPropertyEdit = useCallback(() => {
        setEditingPropertyUid(null)
        setEditingPropertyValue('')
        setPropertyEditErrors({})
    }, [])

    const handleSavePropertyValue = useCallback(async (propertyUid: string, value: any) => {
        try {
            const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)
            let processedValue: any = value

            // Type coercions only — validation is delegated to the backend
            if (propDef) {
                switch (propDef.data_type) {
                    case 'boolean':
                        processedValue = value === 'true'
                        break
                    case 'date':
                        if (value && !isNaN(Date.parse(value))) {
                            if (propDef.properties?.with_time === true) {
                                const dateValue = new Date(value)
                                processedValue = dateValue.toISOString()
                            } else {
                                const dateValue = new Date(value)
                                processedValue = dateValue.toISOString().split('T')[0]
                            }
                        } else if (value) {
                            throw new Error('Please enter a valid date')
                        }
                        break
                    case 'enum':
                        if (propDef.properties?.multiple === true) {
                            processedValue = value ? value.split(',').map((v: string) => v.trim()).filter((v: string) => v) : []
                        } else {
                            processedValue = value
                        }
                        break
                    case 'number':
                        if (value !== '') {
                            processedValue = parseFloat(value)
                            if (isNaN(processedValue)) {
                                throw new Error('Please enter a valid number')
                            }
                        }
                        break
                    case 'json':
                        processedValue = value
                        break
                    default:
                        processedValue = value
                }
            }

            // Delegate validation to the backend
            let finalValue = processedValue
            try {
                const validation = await apiClient.POST('/api/v1/organizations/{orgCode}/properties/{propertyUid}/validate', {
                    params: { path: { orgCode: org, propertyUid } },
                    body: { value: processedValue }
                })
                if (validation.data && validation.data.status !== 'ok') {
                    setPropertyEditErrors(prev => ({ ...prev, [propertyUid]: (validation.data as any).errors?.join('; ') || 'Validation failed' }))
                    return
                }
                if (validation.data?.value !== undefined) {
                    finalValue = validation.data.value
                }
            } catch {
                // Validation endpoint unavailable — proceed with save
            }

            await updatePropertyValueMutation.mutateAsync(propertyUid, { value: finalValue })

            queryClient.invalidateQueries({ queryKey: queryKeys.assetProperties(org, assetUid) })
            setEditingPropertyUid(null)
            setEditingPropertyValue('')
            setPropertyEditErrors({})
            onPropertyUpdated?.()
        } catch (error) {
            const errorMessage = (error as any)?.title || (error as any)?.message || 'Failed to update property'
            setPropertyEditErrors({ [propertyUid]: errorMessage })
        }
    }, [updatePropertyValueMutation, org, assetUid, queryClient, onPropertyUpdated, propertyDefinitions])

    const handleDeletePropertyValue = useCallback(async (propertyUid: string) => {
        setPropertyToDelete(propertyUid)
    }, [])

    const confirmDeletePropertyValue = useCallback(async () => {
        if (!propertyToDelete) return

        try {
            await deletePropertyValueMutation.mutateAsync(propertyToDelete)

            queryClient.invalidateQueries({ queryKey: queryKeys.assetProperties(org, assetUid) })
            setPropertyToDelete(null)
            onPropertyUpdated?.()
        } catch (error) {
            console.error('Failed to delete property value:', error)
        }
    }, [deletePropertyValueMutation, org, assetUid, queryClient, onPropertyUpdated, propertyToDelete])

    const cancelDeletePropertyValue = useCallback(() => {
        setPropertyToDelete(null)
    }, [])

    const handlePropertyKeyDown = useCallback((e: React.KeyboardEvent, propertyUid: string, value: any) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSavePropertyValue(propertyUid, value)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelPropertyEdit()
        }
    }, [handleSavePropertyValue, handleCancelPropertyEdit])

    // Sibling property list handed to the shared value cell so the ISO 20022 address editor
    // can write the `address_raw` / `geolocation` trio in one go.
    const propertySiblings: PropertySibling[] = useMemo(() =>
        properties
            .map((p): PropertySibling | null => {
                const pd = propertyDefinitions.find(d => d.uid === p.property_uid)
                return pd?.code && p.property_uid
                    ? { code: pd.code, key: p.property_uid, value: p.value as unknown }
                    : null
            })
            .filter((s): s is PropertySibling => s !== null),
        [properties, propertyDefinitions]
    )

    const getPropertyDisplayElement = useCallback((value: unknown, propertyUid?: string) => {
        if (value === null || value === undefined) return null

        if (propertyUid && value !== null && value !== undefined) {
            const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)

            // compliance_status is system-managed (computed by the compliance evaluation
            // engine) — link through to the Compliance tab so users go fix the underlying
            // rule instead of expecting an edit here to stick.
            //
            // Note: AssetDetailPage.tsx's tab state (`activeTab`) is local React state
            // re-synced from the URL only when `assetUid` changes (see $assetUid.tsx's
            // `useEffect(() => setActiveTab(...), [assetUid])` — issue #1017 fix), so a
            // same-route `navigate({ search: { tab: ... } })` patches the URL but does not
            // switch the visible tab. A plain anchor (full navigation, mirroring the
            // `view_issue` cross-tab link in AssetCompliance.tsx) reloads the page with the
            // right `?tab=` already in the URL at mount, sidestepping that local-state gap.
            if (propDef?.code === 'compliance_status') {
                const complianceTabHref = `/${org}/assets/${assetUid}?tab=compliance`
                return (
                    <div className="inline-flex items-center gap-1.5">
                        <EnumPropertyDisplay propertyDefinitionUid={propertyUid} value={value as string | string[]} org={org} />
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <a
                                        href={complianceTabHref}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid="compliance-status-view-tab-link"
                                    >
                                        <AppIcon path={mdiOpenInNew} size="xs" />
                                    </a>
                                </TooltipTrigger>
                                <TooltipContent>{t('detail.view_compliance_tab')}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                )
            }

        }

        return (
            <PropertyValueDisplay
                org={org}
                value={value}
                propertyDefinition={propertyUid ? propertyDefinitions.find(pd => pd.uid === propertyUid) : undefined}
                propertyKey={propertyUid || ''}
                siblings={propertySiblings}
                readonly={!canWriteAssets}
                saveProperty={handleSavePropertyValue}
            />
        )
    }, [propertyDefinitions, org, handleSavePropertyValue, propertySiblings, assetUid, t, canWriteAssets])

    // Add property handlers
    const handlePropertyDefinitionSelect = useCallback((option: ApiSearchableSelectOption) => {
        setNewPropertyDefinitionUid(option.value)
        setNewPropertyValue('')
        setAddPropertyError(null)

        // Add this option to additionalOptions if it's not already there
        setAdditionalOptions(prev => {
            const exists = prev.some(opt => opt.value === option.value)
            if (!exists) {
                return [...prev, option]
            }
            return prev
        })
    }, [])

    const handleAddProperty = useCallback(async () => {
        if (!newPropertyDefinitionUid) return

        try {
            const selectedPropDef = propertyDefinitionsAll.find(pd => pd.uid === newPropertyDefinitionUid)
            let processedValue: any = newPropertyValue

            // Process value based on property type
            if (selectedPropDef) {
                switch (selectedPropDef.data_type) {
                    case 'boolean':
                        if (!newPropertyValue) {
                            setAddPropertyError('Please select a boolean value')
                            return
                        }
                        processedValue = newPropertyValue === 'true'
                        break
                    case 'date':
                        if (Array.isArray(newPropertyValue)) {
                            setAddPropertyError('Multiple values not supported for date type')
                            return
                        }
                        if (newPropertyValue && !isNaN(Date.parse(newPropertyValue))) {
                            // Handle datetime-local input values which are in local timezone
                            if (selectedPropDef.properties?.with_time === true) {
                                // For datetime-local, the value is in format YYYY-MM-DDTHH:mm
                                // We need to interpret this as local time and convert to UTC
                                const dateValue = new Date(newPropertyValue)
                                processedValue = dateValue.toISOString()
                            } else {
                                // For date-only, just use the date part
                                const dateValue = new Date(newPropertyValue)
                                processedValue = dateValue.toISOString().split('T')[0]
                            }
                        } else {
                            setAddPropertyError('Please enter a valid date')
                            return
                        }
                        break
                    case 'enum':
                        // For multiple enum values, convert comma-separated string to array
                        if (selectedPropDef.properties?.multiple === true) {
                            processedValue = newPropertyValue ? newPropertyValue.split(',').map(v => v.trim()).filter(v => v) : []
                        } else {
                            processedValue = newPropertyValue
                        }
                        break
                    case 'number':
                        if (newPropertyValue !== '') {
                            processedValue = parseFloat(newPropertyValue)
                            if (isNaN(processedValue)) {
                                setAddPropertyError('Please enter a valid number')
                                return
                            }
                            // Check if integer_only constraint is enabled
                            if (selectedPropDef.properties?.integer_only && !Number.isInteger(processedValue)) {
                                setAddPropertyError('Please enter an integer (whole number)')
                                return
                            }
                            // Check min/max constraints
                            if (typeof selectedPropDef.properties?.min_value === 'number' && processedValue < selectedPropDef.properties.min_value) {
                                setAddPropertyError(`Value must be at least ${selectedPropDef.properties.min_value}`)
                                return
                            }
                            if (typeof selectedPropDef.properties?.max_value === 'number' && processedValue > selectedPropDef.properties.max_value) {
                                setAddPropertyError(`Value must be at most ${selectedPropDef.properties.max_value}`)
                                return
                            }
                        }
                        break
                    case 'text':
                        // Check regex pattern validation
                        if (newPropertyValue && selectedPropDef.properties?.regex_pattern && typeof selectedPropDef.properties.regex_pattern === 'string') {
                            try {
                                const regex = new RegExp(selectedPropDef.properties.regex_pattern)
                                if (!regex.test(newPropertyValue)) {
                                    setAddPropertyError(`Value does not match required pattern: ${selectedPropDef.properties.regex_pattern}`)
                                    return
                                }
                            } catch (regexError) {
                                if (regexError instanceof Error && regexError.message.includes('pattern')) {
                                    setAddPropertyError(regexError.message)
                                    return
                                }
                                setAddPropertyError('Invalid regex pattern in property definition')
                                return
                            }
                        }
                        processedValue = newPropertyValue
                        break
                        case 'json':
                        // For JSON type, parse the string value
                        if (newPropertyValue) {
                            try {
                                processedValue = JSON.parse(newPropertyValue)
                            } catch {
                                setAddPropertyError('Invalid JSON format')
                                return
                            }
                        }
                        break
                    default:
                        processedValue = newPropertyValue
                }
            }

            await setPropertyValueMutation.mutateAsync({
                properties: [{
                    property_uid: newPropertyDefinitionUid,
                    value: processedValue
                }]
            })

            queryClient.invalidateQueries({ queryKey: queryKeys.assetProperties(org, assetUid) })
            setNewPropertyDefinitionUid('')
            setNewPropertyValue('')
            setShowAddPropertyForm(false)
            setAddPropertyError(null)
            onPropertyUpdated?.()
        } catch (error) {
            const errorMessage = (error as any)?.title || (error as any)?.message || 'Failed to add property'
            setAddPropertyError(errorMessage)
        }
    }, [setPropertyValueMutation, org, assetUid, newPropertyDefinitionUid, newPropertyValue, queryClient, onPropertyUpdated, propertyDefinitionsAll])

    const handleCancelAddProperty = useCallback(() => {
        setNewPropertyDefinitionUid('')
        setNewPropertyValue('')
        setShowAddPropertyForm(false)
        setAddPropertyError(null)
    }, [])

    // Enter commits and Escape cancels the inline add-value flow — the same
    // contract as editing an existing value (handlePropertyKeyDown). Not wired
    // on the JSON textarea, where Enter is a newline.
    const handleAddPropertyKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleAddProperty()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelAddProperty()
        }
    }, [handleAddProperty, handleCancelAddProperty])

    // code → value for every scalar text/enum value on the asset. A suggestion
    // list narrowed by `depends_on` reads the brand from here, so the model list
    // follows the brand as soon as it is typed — before it is even saved.
    const propertyContextByCode = useMemo(() => {
        const map: Record<string, string> = {}
        for (const property of properties) {
            // The values payload embeds its own property_definition; reading it
            // from there rather than waiting for the by-uid definitions query
            // means the context is complete on the very first render, so a
            // suggestion list never fires once unnarrowed and then again.
            const propDef = (property as { property_definition?: PropertyDefinition }).property_definition
                ?? propertyDefinitions.find(pd => pd.uid === property.property_uid)
            if (!propDef?.code) continue
            if (propDef.data_type !== 'text' && propDef.data_type !== 'enum') continue
            if (typeof property.value !== 'string') continue
            map[propDef.code] = property.value
        }
        return map
    }, [properties, propertyDefinitions])

    // The context handed to an input excludes the property being edited (a
    // property never narrows itself) and reflects the in-flight edit of any
    // other value.
    const buildPropertyContext = useCallback((editedPropertyUid: string, editedValue?: string) => {
        const editedDef = getPropertyDefinition(editedPropertyUid)
        const ctx = { ...propertyContextByCode }
        if (editedDef?.code) delete ctx[editedDef.code]
        if (editingPropertyUid && editingPropertyUid !== editedPropertyUid) {
            const liveDef = getPropertyDefinition(editingPropertyUid)
            if (liveDef?.code) ctx[liveDef.code] = editingPropertyValue
        }
        void editedValue
        return ctx
    }, [propertyContextByCode, getPropertyDefinition, editingPropertyUid, editingPropertyValue])

    // Render property value input for editing
    const renderEditPropertyInput = useCallback((propertyDefinitionUid: string, value: string, setValue: (value: string) => void, onKeyDown: (e: React.KeyboardEvent) => void) => (
        <PropertyValueEditInput
            org={org}
            propertyDefinition={getPropertyDefinition(propertyDefinitionUid)}
            value={value}
            onChange={setValue}
            onKeyDown={onKeyDown}
            disabled={updatePropertyValueMutation.isPending}
            hasError={Boolean(propertyEditErrors[propertyDefinitionUid])}
            assetTypeUid={assetTypeUid}
            propertyContext={buildPropertyContext(propertyDefinitionUid)}
        />
    ), [getPropertyDefinition, propertyEditErrors, updatePropertyValueMutation.isPending, org, assetTypeUid, buildPropertyContext])

    // Render property value input for add form
    const renderPropertyValueInput = useCallback(() => {
        if (!selectedPropertyDef) return null

        if (selectedPropertyDef.data_type === 'boolean') {
            const semantic = selectedPropertyDef.properties?.semantic as string | undefined
            const options = getBooleanOptions(semantic, tCommon)
            return (
                <select
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    onKeyDown={handleAddPropertyKeyDown}
                    autoFocus
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="property-add-boolean"
                >
                    <option value="">{t('detail.select_value_placeholder')}</option>
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )
        }

        if (selectedPropertyDef.data_type === 'date') {
            const withTime = selectedPropertyDef.properties?.with_time === true
            return (
                <input
                    type={withTime ? "datetime-local" : "date"}
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    onKeyDown={handleAddPropertyKeyDown}
                    onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                    autoFocus
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="property-add-date"
                />
            )
        }

        if (selectedPropertyDef.data_type === 'enum') {
            return <EnumPropertyInput propertyDefinitionUid={selectedPropertyDef.uid} value={newPropertyValue} onChange={setNewPropertyValue} org={org} />
        }

        // For number inputs with integer constraint
        if (selectedPropertyDef.data_type === 'number') {
            return (
                <input
                    type="number"
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    onKeyDown={handleAddPropertyKeyDown}
                    autoFocus
                    data-testid="property-add-number"
                    step={selectedPropertyDef.properties?.integer_only ? "1" : "any"}
                    min={typeof selectedPropertyDef.properties?.min_value === 'number' ? selectedPropertyDef.properties.min_value : undefined}
                    max={typeof selectedPropertyDef.properties?.max_value === 'number' ? selectedPropertyDef.properties.max_value : undefined}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={`Enter ${selectedPropertyDef.properties?.integer_only ? 'integer' : 'number'}${selectedPropertyDef.properties?.unit ? ` (${selectedPropertyDef.properties.unit})` : ''}...`}
                />
            )
        }

        // Text goes through PropertyValueEditInput rather than a second input of
        // its own: the duplication is what kept the add form drifting away from
        // the inline editor (regex hint, and now value suggestions).
        if (selectedPropertyDef.data_type === 'text') {
            return (
                <PropertyValueEditInput
                    org={org}
                    propertyDefinition={selectedPropertyDef as PropertyDefinition}
                    value={newPropertyValue}
                    onChange={setNewPropertyValue}
                    onKeyDown={handleAddPropertyKeyDown}
                    assetTypeUid={assetTypeUid}
                    propertyContext={buildPropertyContext(selectedPropertyDef.uid || '')}
                    data-testid="property-add-text"
                    autoFocus
                />
            )
        }

        // JSON type input
        if (selectedPropertyDef.data_type === 'json') {
            return (
                <Textarea
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-mono"
                    placeholder={t('detail.enter_json')}
                    spellCheck={false}
                />
            )
        }

        // Default input for other types
        return (
            <input
                type="text"
                value={newPropertyValue}
                onChange={(e) => setNewPropertyValue(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder={t('detail.enter_value')}
            />
        )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPropertyDef, newPropertyValue, org, t, handleAddPropertyKeyDown, assetTypeUid, buildPropertyContext])

    // ---- Row cell renderers (shared by the table + reliability variants) ----
    const renderRowName = (property: any) => (
        <div>
            <div className="flex items-center gap-1">
                {getPropertyDefinitionName(property.property_uid || '')}
                {(() => {
                    const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
                    return propDef?.flags?.includes('system_managed') ? (
                        <span title="System managed">
                            <AppIcon tone="muted" path={mdiLock} size="xs" />
                        </span>
                    ) : null
                })()}
            </div>
            {(() => {
                const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
                const constraints = getPropertyConstraints(propDef, tCommon)
                return constraints.length > 0 ? (
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                        {constraints.join(' • ')}
                    </div>
                ) : null
            })()}
        </div>
    )

    const renderRowValueInner = (property: any) => (
        editingPropertyUid === property.property_uid ? (
            <div>
                <div className="flex items-center gap-2">
                    {renderEditPropertyInput(
                        property.property_uid || '',
                        editingPropertyValue,
                        setEditingPropertyValue,
                        (e) => handlePropertyKeyDown(e, property.property_uid || '', editingPropertyValue)
                    )}
                    <Button
                        onClick={() => handleSavePropertyValue(property.property_uid || '', editingPropertyValue)}
                        disabled={updatePropertyValueMutation.isPending}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                        data-testid="save-property-value"
                    >
                        {updatePropertyValueMutation.isPending ? '...' : <AppIcon path={mdiCheck} size="xs" />}
                    </Button>
                    <Button
                        onClick={handleCancelPropertyEdit}
                        disabled={updatePropertyValueMutation.isPending}
                        variant="outline"
                        className="px-2 py-1 text-sm"
                        data-testid="cancel-property-edit"
                    >
                        <AppIcon path={mdiClose} size="sm" />
                    </Button>
                </div>
                {propertyEditErrors[property.property_uid || ''] && (
                    <p className="mt-1 text-xs text-destructive">{propertyEditErrors[property.property_uid || '']}</p>
                )}
            </div>
        ) : (
            <div className="flex items-center justify-between group">
                <span
                    className={`${(() => {
                        const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
                        return !canWriteAssets || usesDialogEditor(propDef, property.value) || propDef?.flags?.includes('system_managed')
                            ? 'cursor-default p-1 rounded transition-colors flex-1'
                            : 'cursor-pointer hover:bg-muted/50 p-1 rounded transition-colors flex-1'
                    })()}`}
                    onClick={() => {
                        if (!canWriteAssets) return
                        const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
                        if (!usesDialogEditor(propDef, property.value) && !propDef?.flags?.includes('system_managed')) {
                            startEditingProperty(property)
                        }
                    }}
                    title={(() => {
                        const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
                        if (propDef?.flags?.includes('system_managed')) return t('detail.system_managed')
                        return usesDialogEditor(propDef, property.value)
                            ? t('detail.json_edit_only')
                            : t('detail.click_to_edit')
                    })()}
                    data-testid="edit-property-value"
                >
                    {getPropertyDisplayElement(property.value, property.property_uid || undefined) || t('detail.click_to_edit')}
                </span>
                {canWriteAssets && property.flags?.includes('auto_propagated') && (
                    <Button
                        onClick={() => confirmPropertyValueMutation.mutate(property.property_uid || '')}
                        disabled={confirmPropertyValueMutation.isPending}
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto text-primary hover:text-primary hover:bg-primary/10"
                        title={t('detail.confirm_value')}
                        data-testid="confirm-propagated-property-btn"
                    >
                        <AppIcon path={mdiCheck} size="xs" />
                    </Button>
                )}
                <Button
                    onClick={() => openHistoryDrawer(property.property_uid || '')}
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto text-muted-foreground hover:text-foreground hover:bg-muted"
                    title={t('detail.view_history')}
                    data-testid="view-property-history"
                >
                    <AppIcon path={mdiHistory} size="xs" />
                </Button>
                {canWriteAssets && !propertyDefinitions.find(pd => pd.uid === property.property_uid)?.flags?.includes('system_managed') && (
                    <Button
                        onClick={() => handleDeletePropertyValue(property.property_uid || '')}
                        disabled={deletePropertyValueMutation.isPending}
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                        title={t('detail.delete_property_value')}
                        data-testid="delete-property-value"
                    >
                        <AppIcon path={mdiDelete} size="xs" />
                    </Button>
                )}
            </div>
        )
    )

    const renderRowSource = (property: any, size: 'sm' | 'md' = 'sm') => (
        <PropertySourceBadge
            org={org}
            size={size}
            origin={{ kind: 'asset', assetUid, propertyUid: property.property_uid || undefined }}
            sourceCode={(property as any).source_code}
            sourceDetails={(property as any).source_details}
            createdByName={(property as any).created_by_name}
            updatedByName={(property as any).updated_by_name}
            approvedByName={(property as any).approved_by_name}
            approvedAt={(property as any).approved_at}
            flags={property.flags ?? undefined}
            onConfirm={property.flags?.includes('auto_propagated')
                ? () => confirmPropertyValueMutation.mutate(property.property_uid || '')
                : undefined}
            confirmPending={confirmPropertyValueMutation.isPending}
            // Auto-merged (classify() resolved it without a human approval
            // click) iff it came through document extraction and carries no
            // approver. Rows are already filtered to non-pending (excludes
            // to_approve), so "no approver" here specifically means
            // classify() -> AUTO, never a value still awaiting review.
            isAutoMerged={
                ((property as any).source_code === 'document_extraction' || (property as any).source_code === 'ai_extraction') &&
                !(property as any).approved_by_uid
            }
        />
    )

    // Add-property button + form, shared by both variants.
    const addPropertyButton = !canWriteAssets ? null : (
        <Button
            onClick={() => setShowAddPropertyForm(!showAddPropertyForm)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm"
            aria-label={t('detail.add_property')}
            title={t('detail.add_property')}
            data-testid="asset-properties-add-property-btn"
        >
            <AppIcon path={mdiPlus} size="md" className="md:mr-1" />
            <span className="hidden md:inline">{t('detail.add_property')}</span>
        </Button>
    )

    const addPropertyFormEl = showAddPropertyForm ? (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
            <h4 className="text-md font-medium text-foreground mb-3">{t('detail.add_new_property')}</h4>
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                        {t('detail.property_definition')}
                    </label>
                    <ApiSearchableSelect
                        value={newPropertyDefinitionUid}
                        onValueChange={setNewPropertyDefinitionUid}
                        onOptionSelect={handlePropertyDefinitionSelect}
                        onSearch={searchPropertyDefinitions}
                        placeholder={t('detail.select_property')}
                        searchPlaceholder={t('detail.search_property_definitions')}
                        emptyMessage={t('detail.no_property_definitions')}
                        initialOptions={propertyDefinitionInitialOptions}
                        allOptions={additionalOptions}
                        className="w-full"
                        data-testid="property-definition-select"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                        {t('detail.value')}
                    </label>
                    {renderPropertyValueInput()}
                </div>
            </div>
            {addPropertyError && (
                <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive">{addPropertyError}</p>
                </div>
            )}
            <div className="flex gap-2 mt-4">
                <Button
                    onClick={handleAddProperty}
                    disabled={setPropertyValueMutation.isPending || !newPropertyDefinitionUid}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {setPropertyValueMutation.isPending ? t('detail.adding') : t('detail.add_property')}
                </Button>
                <Button
                    onClick={handleCancelAddProperty}
                    disabled={setPropertyValueMutation.isPending}
                    variant="outline"
                    className="px-4 py-2 text-sm"
                >
                    {t('detail.cancel')}
                </Button>
            </div>
        </div>
    ) : null

    const dialogsEl = (
        <>
            <PropertyHistoryDrawer
                org={org}
                assetUid={assetUid}
                propertyUid={historyPropertyUid}
                candidates={historyPropertyUid ? (pendingByDef.get(historyPropertyUid) ?? []) : []}
                onClose={closeHistoryDrawer}
            />
            {/* Confirmation Dialog for Deleting Property */}
            <AlertDialog open={propertyToDelete !== null} onOpenChange={(open) => !open && cancelDeletePropertyValue()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('detail.delete_property_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('detail.delete_property_confirm')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelDeletePropertyValue}>{t('detail.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeletePropertyValue}
                            className="bg-destructive hover:bg-destructive/90"
                        >
                            {t('detail.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )

    // ---- Data-tab grouped rendering (table variant, groupByCategory) ----
    // Ghost (empty base property) value cell — inline add flow.
    const renderGhostAdd = (def: PropertyDefinition) => (
        newPropertyDefinitionUid === def.uid ? (
            <div className="flex items-center gap-2">
                <div className="flex-1">{renderPropertyValueInput()}</div>
                <Button
                    onClick={handleAddProperty}
                    disabled={setPropertyValueMutation.isPending}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                    data-testid={`data-save-${def.code}`}
                    title={t('detail.add_property')}
                >
                    {setPropertyValueMutation.isPending ? '...' : <AppIcon path={mdiCheck} size="xs" />}
                </Button>
                <Button
                    onClick={handleCancelAddProperty}
                    disabled={setPropertyValueMutation.isPending}
                    variant="outline"
                    className="px-2 py-1 text-sm"
                    data-testid={`data-cancel-${def.code}`}
                >
                    <AppIcon path={mdiClose} size="sm" />
                </Button>
            </div>
        ) : def.code === SERIES_ONLY_PROPERTY ? (
            <span className="p-1 text-sm italic text-muted-foreground" data-testid={`data-add-${def.code}`}>
                {t('meter_readings.use_card')}
            </span>
        ) : (
            <button
                type="button"
                onClick={() => { setNewPropertyDefinitionUid(def.uid || ''); setNewPropertyValue(''); setAddPropertyError(null) }}
                className="rounded p-1 text-sm italic text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                data-testid={`data-add-${def.code}`}
            >
                {tData('asset.empty_add')}
            </button>
        )
    )

    // ---- Core asset-column rows (external code, lifecycle dates) ----
    // Uid-valued core rows (parent, type) rest as text; one click opens the picker.
    const [pickingCoreCode, setPickingCoreCode] = useState<string | null>(null)
    // Delayed so a click on the picker's own clear button, which starts with the
    // outside-pointer dismissal, still lands before the picker unmounts.
    const dismissCorePicker = () => { window.setTimeout(() => setPickingCoreCode(null), 250) }
    const startEditingCore = (cr: CoreRow) => { setEditingCoreCode(cr.code); setEditingCoreValue(cr.value || ''); setCoreError(null) }
    const cancelEditingCore = () => { setEditingCoreCode(null); setEditingCoreValue(''); setCoreError(null) }
    const saveCoreValue = async (code: string, value: string | null) => {
        if (!onSaveCoreRow) return
        setCoreSaving(true); setCoreError(null)
        try {
            await onSaveCoreRow(code, value)
            setEditingCoreCode(null); setEditingCoreValue(''); setPickingCoreCode(null)
        } catch (e) {
            setCoreError(e instanceof Error ? e.message : String(e))
        } finally {
            setCoreSaving(false)
        }
    }
    const saveCore = (code: string) => saveCoreValue(code, editingCoreValue.trim() === '' ? null : editingCoreValue)

    // Asset search for uid-valued core rows (the parent). Self AND every
    // descendant are filtered out: the backend refuses both with a 400 (spec
    // 2026-09-18-01), a cycle being what stalls the organization's whole
    // asset_hierarchy job. Offering them would turn a rule into an error
    // message — and a descendant is precisely the mistake someone makes while
    // restructuring.
    const searchCoreAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: { path: { orgCode: org }, query: { limit: '50', ...(query ? { q: query } : {}) } },
            })
            const assets = ((response as { data?: Array<Record<string, unknown>> })?.data) || []
            return assets
                .filter((a) => !excludedParentUids.has(String(a.uid)))
                .map((a) => ({
                    value: String(a.uid),
                    label: String(a.name || a.code || a.uid),
                }))
        } catch (e) {
            console.error('Core asset search failed:', e)
            return []
        }
    }, [request, org, excludedParentUids])

    const renderCoreValue = (cr: CoreRow) => {
        // Read-only first. The `asset` and `asset_type` branches below return
        // their own pickers, so a guard placed after them never ran: a user
        // without `assets.write` was shown a live parent/type selector whose
        // every pick answered 403 (spec 2026-09-18-01).
        if (!canWriteAssets || cr.readOnly) {
            return (
                <span className="px-1 py-0.5" data-testid={`core-value-${cr.code}`}>
                    {cr.valueLabel || cr.value || <span className="text-muted-foreground">—</span>}
                    {cr.hint && (
                        <span className="block text-xs text-muted-foreground" data-testid={`core-hint-${cr.code}`}>
                            {cr.hint}
                        </span>
                    )}
                </span>
            )
        }
        // Uid-valued rows are their own click-to-open editors, so they need none
        // of the input/save/cancel dance — picking saves straight away.
        if ((cr.dataType === 'asset' || cr.dataType === 'asset_type') && pickingCoreCode !== cr.code) {
            return (
                <div>
                    <span
                        className="-mx-1 cursor-pointer rounded px-1 py-0.5 transition-colors hover:bg-muted/50"
                        role="button"
                        tabIndex={0}
                        onClick={() => { setCoreError(null); setPickingCoreCode(cr.code) }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setCoreError(null)
                                setPickingCoreCode(cr.code)
                            }
                        }}
                        title={t('detail.click_to_edit')}
                        data-testid={`core-value-${cr.code}`}
                    >
                        {cr.valueLabel || cr.value || <span className="text-muted-foreground">{t('detail.click_to_edit')}</span>}
                    </span>
                    {coreError && <p className="mt-1 text-xs text-destructive">{coreError}</p>}
                </div>
            )
        }
        if (cr.dataType === 'asset') {
            return (
                <div>
                    <ApiSearchableSelect
                        defaultOpen
                        onDismiss={dismissCorePicker}
                        value={cr.value || ''}
                        placeholder={t('detail.no_parent_asset', { defaultValue: '—' })}
                        searchPlaceholder={`${cr.label}…`}
                        emptyMessage={cr.label}
                        onSearch={searchCoreAssets}
                        initialOptions={cr.value && cr.valueLabel
                            ? [{ value: cr.value, label: cr.valueLabel }]
                            : []}
                        onValueChange={(uid) => saveCoreValue(cr.code, uid || null)}
                        disabled={coreSaving}
                        minSearchLength={2}
                        debounceMs={150}
                        className="max-w-sm"
                        data-testid={`core-picker-${cr.code}`}
                    />
                    {coreError && <p className="mt-1 text-xs text-destructive">{coreError}</p>}
                </div>
            )
        }
        if (cr.dataType === 'asset_type') {
            return (
                <div>
                    <AssetTypePicker
                        org={org}
                        defaultOpen
                        onDismiss={dismissCorePicker}
                        value={cr.value || ''}
                        onChange={(uid) => saveCoreValue(cr.code, uid)}
                        disabled={coreSaving}
                        ariaLabel={cr.label}
                        triggerTestId={`core-picker-${cr.code}`}
                    />
                    {coreError && <p className="mt-1 text-xs text-destructive">{coreError}</p>}
                </div>
            )
        }
        if (editingCoreCode === cr.code) {
            return (
                <div>
                    <div className="flex items-center gap-2">
                        <input
                            type={cr.dataType === 'date' ? 'date' : cr.dataType === 'number' ? 'number' : 'text'}
                            min={cr.dataType === 'number' ? 1 : undefined}
                            step={cr.dataType === 'number' ? 1 : undefined}
                            value={editingCoreValue}
                            onChange={(e) => setEditingCoreValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveCore(cr.code); if (e.key === 'Escape') cancelEditingCore() }}
                            className="flex-1 min-w-0 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            autoFocus
                            disabled={coreSaving}
                            aria-label={cr.label}
                            data-testid={`core-input-${cr.code}`}
                        />
                        <Button
                            onClick={() => saveCore(cr.code)}
                            disabled={coreSaving}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                            data-testid={`core-save-${cr.code}`}
                        >
                            {coreSaving ? '...' : <AppIcon path={mdiCheck} size="sm" />}
                        </Button>
                        <Button onClick={cancelEditingCore} disabled={coreSaving} variant="outline" className="px-2 py-1 text-sm" data-testid={`core-cancel-${cr.code}`}>
                            <AppIcon path={mdiClose} size="sm" />
                        </Button>
                    </div>
                    {coreError && <p className="mt-1 text-xs text-destructive">{coreError}</p>}
                </div>
            )
        }
        return (
            <span
                className="-mx-1 cursor-pointer rounded px-1 py-0.5 transition-colors hover:bg-muted/50"
                onClick={() => startEditingCore(cr)}
                title={t('detail.click_to_edit')}
                data-testid={`core-value-${cr.code}`}
            >
                {cr.value || <span className="text-muted-foreground">{t('detail.click_to_edit')}</span>}
            </span>
        )
    }

    // Value cell for a filled property. No delete / history / confirm buttons —
    // those live in the dedicated Source and History columns of the Data table.
    const renderDataValue = (property: any) => {
        const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
        const isSystem = propDef?.flags?.includes('system_managed')
        const isJson = usesDialogEditor(propDef, property.value)
        const isSeries = propDef?.code === SERIES_ONLY_PROPERTY
        const editable = canWriteAssets && !isSystem && !isJson && !isSeries
        if (editingPropertyUid === property.property_uid) {
            return (
                <div>
                    <div className="flex items-center gap-2">
                        {renderEditPropertyInput(
                            property.property_uid || '',
                            editingPropertyValue,
                            setEditingPropertyValue,
                            (e) => handlePropertyKeyDown(e, property.property_uid || '', editingPropertyValue),
                        )}
                        <Button
                            onClick={() => handleSavePropertyValue(property.property_uid || '', editingPropertyValue)}
                            disabled={updatePropertyValueMutation.isPending}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                            data-testid="save-property-value"
                        >
                            {updatePropertyValueMutation.isPending ? '...' : <AppIcon path={mdiCheck} size="xs" />}
                        </Button>
                        <Button onClick={handleCancelPropertyEdit} disabled={updatePropertyValueMutation.isPending} variant="outline" className="px-2 py-1 text-sm" data-testid="cancel-property-edit">
                            <AppIcon path={mdiClose} size="sm" />
                        </Button>
                    </div>
                    {propertyEditErrors[property.property_uid || ''] && (
                        <p className="mt-1 text-xs text-destructive">{propertyEditErrors[property.property_uid || '']}</p>
                    )}
                </div>
            )
        }
        return (
            <div className="flex flex-col items-start gap-1.5">
                <span
                    className={editable ? '-mx-1 cursor-pointer rounded px-1 py-0.5 transition-colors hover:bg-muted/50' : 'cursor-default'}
                    onClick={editable ? () => startEditingProperty(property) : undefined}
                    title={isSeries ? t('meter_readings.use_card') : isSystem ? t('detail.system_managed') : isJson ? t('detail.json_edit_only') : t('detail.click_to_edit')}
                    data-testid="edit-property-value"
                >
                    {getPropertyDisplayElement(property.value, property.property_uid || undefined) || <span className="text-muted-foreground">{t('detail.click_to_edit')}</span>}
                </span>
                {renderConflictBadge(property.property_uid)}
                {renderReconciliationBadge(property.property_uid)}
            </div>
        )
    }

    // Inviting conflict indicator: an amber "value to arbitrate" pill shown in
    // the Value column. Clicking opens the history drawer, where the proposed
    // and current values are compared and one is adopted.
    const renderConflictBadge = (propertyUid?: string) => {
        if (!propertyUid) return null
        const conflicts = pendingByDef.get(propertyUid)
        if (!conflicts || conflicts.length === 0) return null
        const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)
        return (
            <ConflictBadge
                org={org}
                propertyUid={propertyUid}
                isEnum={propDef?.data_type === 'enum'}
                value={conflicts[0].value}
                fallbackLabel={getPropertyDisplayValue(conflicts[0].value, propertyUid)}
                onOpen={() => openHistoryDrawer(propertyUid)}
            />
        )
    }

    // Neutral history icon (last column). The conflict is signalled by the
    // amber badge in the Value column, not here.
    const renderDataHistory = (propertyUid?: string) => {
        if (!propertyUid) return null
        return (
            <button
                type="button"
                onClick={() => openHistoryDrawer(propertyUid)}
                title={t('detail.view_history')}
                aria-label={t('detail.view_history')}
                data-testid={`data-history-${propertyUid}`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
                <AppIcon path={mdiHistory} size="md" />
            </button>
        )
    }

    // Map property definition uid -> code, so filled base rows can be matched
    // back to their baseMeta (requirement + expected_source hints).
    const codeByUid = useMemo(() => {
        const m = new Map<string, string>()
        for (const d of propertyDefinitionsAll) if (d.uid && d.code) m.set(d.uid, d.code)
        return m
    }, [propertyDefinitionsAll])

    // Declared-vs-observed mismatch indicator (spec
    // 2026-07-24-04-declared-vs-observed-property-reconciliation.md): a small
    // "≠ observé : N" pill next to a declared property that has an open
    // reconciliation issue, linking through to that issue (full navigation,
    // mirroring the compliance-status cross-tab link above).
    const renderReconciliationBadge = (propertyUid?: string) => {
        if (!propertyUid) return null
        const code = codeByUid.get(propertyUid)
        if (!code) return null
        const reconciliation = reconciliationByCode.get(code)
        if (!reconciliation) return null
        return (
            <a
                href={`/${org}/issues/${reconciliation.issueUid}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-semibold text-destructive ring-1 ring-inset ring-destructive/30 transition-colors hover:bg-destructive/20"
                data-testid={`reconciliation-badge-${propertyUid}`}
                title={tData('asset.reconciliation_tooltip')}
            >
                <AppIcon path={mdiAlertOutline} size="xs" className="flex-shrink-0" />
                <span className="truncate">
                    {tData('asset.reconciliation_badge', { value: reconciliation.observedValue })}
                </span>
            </a>
        )
    }

    // Small hints shown next to a base property's name: a mandatory marker and
    // an "expected source" badge (where the value should come from). Read-only.
    const renderBaseHint = (code?: string) => {
        if (!code) return null
        const meta = baseMeta?.[code]
        if (!meta) return null
        return (
            <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
                {meta.requirement === 'mandatory' && (
                    <span
                        className="font-semibold text-destructive"
                        title={tData('asset.requirement.mandatory')}
                        aria-label={tData('asset.requirement.mandatory')}
                        data-testid={`req-mandatory-${code}`}
                    >
                        *
                    </span>
                )}
                {meta.expectedSource && (
                    <span
                        className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                        title={tData(`asset.expected_source.${meta.expectedSource}`, { defaultValue: meta.expectedSource })}
                        data-testid={`expected-source-${code}`}
                    >
                        {tData(`asset.expected_source_short.${meta.expectedSource}`, { defaultValue: meta.expectedSource })}
                    </span>
                )}
            </span>
        )
    }

    const groupedEmptyMsg = !showAll && hiddenEmptyCount > 0
        ? tData('asset.all_empty_hidden')
        : tData('asset.empty_all')
    const groupedEl = (
        displaySections.length === 0 ? (
            <EmptyState variant="inline" title={groupedEmptyMsg} data-testid="asset-data-groups" />
        ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm" data-testid="asset-data-groups">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        <col className="w-[28%]" />
                        <col className="w-[40%]" />
                        <col className="w-[22%]" />
                        <col className="w-[10%]" />
                    </colgroup>
                    <thead>
                        <tr className="border-b border-border bg-muted/50 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            <th className="px-3.5 py-2.5 text-left">{tData('asset.col_property')}</th>
                            <th className="px-3.5 py-2.5 text-left">{tData('asset.col_value')}</th>
                            <th className="px-3.5 py-2.5 text-left">{tData('asset.col_source')}</th>
                            <th className="px-2 py-2.5 text-center" title={tData('asset.col_history')} aria-label={tData('asset.col_history')}>
                                <AppIcon tone="muted" path={mdiHistory} size="sm" className="mx-auto" />
                            </th>
                        </tr>
                    </thead>
                    {displaySections.map((section) => (
                        <tbody key={section.category} data-testid={`data-section-${section.category}`}>
                            <tr className="border-y border-border/60 bg-muted/30">
                                <td colSpan={4} className="px-3.5 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                    {tData(`asset.category.${section.category}`, { defaultValue: section.category })}
                                </td>
                            </tr>
                            {section.rows.map((row, i) => {
                                if (row.kind === 'core') {
                                    const cr = row.core
                                    return (
                                        <tr key={`c-${cr.code}`} className="border-b border-border/40 align-middle last:border-b-0 hover:bg-muted/20" data-testid={`data-core-${cr.code}`}>
                                            <td className="px-3.5 py-3.5 text-sm font-medium text-foreground">{cr.label}</td>
                                            <td className="px-3.5 py-3.5 text-sm text-foreground">{renderCoreValue(cr)}</td>
                                            <td className="px-3.5 py-3.5 text-xs text-muted-foreground">{tData('asset.source_core', { defaultValue: 'Fiche actif' })}</td>
                                            <td className="px-2 py-3.5 text-center"></td>
                                        </tr>
                                    )
                                }
                                if (row.kind === 'value') {
                                    const uid = row.property.property_uid as string | undefined
                                    return (
                                        <tr
                                            key={`v-${uid}`}
                                            className="border-b border-border/40 align-middle last:border-b-0 hover:bg-muted/20"
                                            data-testid={uid ? `asset-property-row-${uid}` : undefined}
                                        >
                                            <td className="px-3.5 py-3.5 text-sm font-medium text-foreground">{renderRowName(row.property)}{renderBaseHint(codeByUid.get(uid || ''))}</td>
                                            <td className="px-3.5 py-3.5 text-sm text-foreground">{renderDataValue(row.property)}</td>
                                            <td className="px-3.5 py-3.5">{renderRowSource(row.property, 'md')}</td>
                                            <td className="px-2 py-3.5 text-center">{renderDataHistory(uid)}</td>
                                        </tr>
                                    )
                                }
                                const def = row.def
                                const pending = def.uid ? pendingByDef.get(def.uid) : undefined
                                const hasConflict = Boolean(pending && pending.length)
                                return (
                                    <tr key={`g-${def.code}-${i}`} className="border-b border-border/40 align-middle last:border-b-0 hover:bg-muted/20" data-testid={`data-ghost-${def.code}`}>
                                        <td className="px-3.5 py-3.5 text-sm font-medium text-muted-foreground">{localizeDefName(def)}{renderBaseHint(def.code)}</td>
                                        <td className="px-3.5 py-3.5 text-sm">
                                            {hasConflict ? (
                                                <div className="flex flex-col items-start gap-1.5">
                                                    <span className="text-muted-foreground">
                                                        {def.data_type === 'enum'
                                                            ? <EnumPropertyDisplay propertyDefinitionUid={def.uid} value={pending![0].value as string | string[]} org={org} />
                                                            : getPropertyDisplayValue(pending![0].value, def.uid)}
                                                    </span>
                                                    {renderConflictBadge(def.uid)}
                                                </div>
                                            ) : renderGhostAdd(def)}
                                        </td>
                                        <td className="px-3.5 py-3.5">{hasConflict ? renderRowSource(pending![0], 'md') : null}</td>
                                        <td className="px-2 py-3.5 text-center">{hasConflict ? renderDataHistory(def.uid) : null}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    ))}
                </table>
              </div>
            </div>
        )
    )

    // ------------------------ Reliability variant --------------------------
    // "Validé automatiquement" collapsible: one editable row per property,
    // styled like the Fiabilisation prototype (name + value + source).
    if (variant === 'reliability') {
        return (
            <div data-testid="dr-validated-props">
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                    <div className="flex items-center gap-2 px-4 py-3">
                        <button
                            type="button"
                            onClick={() => setValidatedOpen((o) => !o)}
                            className="flex flex-1 items-center gap-2 text-left"
                            data-testid="dr-validated-toggle"
                            aria-expanded={validatedOpen}
                        >
                            <AppIcon path={mdiCheck} size="sm" className="text-success" />
                            <span className="text-sm font-semibold text-foreground">{tReliability('immeuble.auto_validated')}</span>
                            <span className="text-sm text-muted-foreground">
                                · {tReliability('immeuble.auto_validated_count', { count: visibleProperties.length })}
                            </span>
                            <AppIcon
                                path={mdiChevronDown}
                                size="sm"
                                className={`ml-auto text-muted-foreground transition-transform ${validatedOpen ? 'rotate-180' : ''}`}
                            />
                        </button>
                        {addPropertyButton}
                    </div>

                    {validatedOpen && (
                        <div className="border-t border-border/60 p-4">
                            {addPropertyFormEl}
                            {propertiesLoading ? (
                                <div className="text-sm text-muted-foreground">{t('detail.loading_properties')}</div>
                            ) : visibleProperties.length === 0 ? (
                                <EmptyState variant="inline" className="py-3" title={tReliability('immeuble.no_auto_validated')} />
                            ) : (
                                <div className="divide-y divide-border/60">
                                    {visibleProperties.map((property) => (
                                        <div
                                            key={property.property_uid}
                                            className="flex flex-col gap-2 py-3 @lg:flex-row @lg:items-start @lg:justify-between @lg:gap-6"
                                            data-testid={`dr-validated-row-${property.property_uid}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-foreground">{renderRowName(property)}</div>
                                                <div className="mt-1 text-sm text-muted-foreground">{renderRowValueInner(property)}</div>
                                            </div>
                                            <div className="flex-shrink-0 pt-0.5">{renderRowSource(property)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {dialogsEl}
            </div>
        )
    }

    // ---- Data-tab grouped variant: no big card chrome (the AssetDataTab
    // wrapper owns the page header); render category sections + the add flow
    // for properties beyond the base set. ----
    if (groupByCategory) {
        // Pending `to_approve` candidates are surfaced inline per-row (the
        // warning icon in the History column → conflict resolution in the
        // history drawer), so the standalone pending-approvals banner is
        // suppressed here to avoid duplicating the queue.
        return (
            <div data-testid="asset-data-properties">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    {hideEmptyByDefault ? (
                        <button
                            type="button"
                            onClick={() => setShowAll(!showAll)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
                            data-testid="asset-data-show-all-toggle"
                            aria-pressed={showAll}
                        >
                            <AppIcon tone="muted" path={showAll ? mdiEyeOff : mdiEye} size="sm" />
                            <span>{showAll ? tData('asset.show_filled') : tData('asset.show_all')}</span>
                            {!showAll && hiddenEmptyCount > 0 && (
                                <span className="text-xs font-normal text-muted-foreground" data-testid="asset-data-hidden-count">
                                    · {tData('asset.empty_hidden', { count: hiddenEmptyCount })}
                                </span>
                            )}
                        </button>
                    ) : <span />}
                    {addPropertyButton}
                </div>
                {addPropertyFormEl}
                {propertiesLoading ? (
                    <div className="text-muted-foreground">{t('detail.loading_properties')}</div>
                ) : (
                    groupedEl
                )}
                {dialogsEl}
            </div>
        )
    }

    return (
        <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-foreground">{t('detail.properties')}</h3>
                {addPropertyButton}
            </div>

            {addPropertyFormEl}

            {propertiesLoading ? (
                <div className="text-muted-foreground">{t('detail.loading_properties')}</div>
            ) : visibleProperties.length === 0 ? (
                <EmptyState variant="inline" title={t('detail.no_properties')} />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('detail.property')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('detail.value')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider w-[120px]">
                                    {t('detail.source')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                            {visibleProperties.map((property) => (
                                <tr key={property.property_uid} className="hover:bg-muted/50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        {renderRowName(property)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        {renderRowValueInner(property)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {renderRowSource(property)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {dialogsEl}
        </div>
    )
}
/**
 * The amber « value to arbitrate » pill of the Value column. An enum value is
 * printed by its label, never by its code (« Présent, confiné », not
 * `present_contained`); clicking opens the history drawer, where the proposed
 * and current values are compared and one is adopted.
 */
function ConflictBadge({ org, propertyUid, isEnum, value, fallbackLabel, onOpen }: {
    org: string
    propertyUid: string
    isEnum: boolean
    value: unknown
    fallbackLabel: React.ReactNode
    onOpen: () => void
}) {
    const { t: tData } = useTranslation('data')
    const enumText = useEnumValueText(org, isEnum ? propertyUid : undefined, value)
    const label = isEnum ? enumText : fallbackLabel
    return (
        <button
            type="button"
            onClick={onOpen}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning ring-1 ring-inset ring-warning/30 transition-colors hover:bg-warning/25"
            data-testid={`data-conflict-${propertyUid}`}
            data-conflict-label={typeof label === 'string' ? label : undefined}
            title={tData('asset.conflict_tooltip')}
        >
            <AppIcon path={mdiAlertOutline} size="xs" className="flex-shrink-0" />
            <span className="truncate">{tData('asset.conflict_badge', { value: label })}</span>
        </button>
    )
}
